import { type MutableRefObject, useEffect, useMemo } from "react";
import {
  ConversationMessage,
  Outcome,
  ProductTree,
  SessionVersion,
} from "@financial-advisory/data-access/api-types";
import {
  useGetInitialMessagesQuery,
  useGetProductTreesQuery,
  useGetSessionAllVersionsQuery,
} from "@financial-advisory/data-access/financial-advisory-api-service";
import { PageSpinner } from "@financial-advisory/ui/components/spinners";
import {
  type ChangedNode,
  type QuestionAnswer,
  type TreeValue,
  cleanTreeNames,
  deduplicateTreesByQuestion,
  getDedupedLeafNodes,
  getSystemStateValues,
  getTreeTypeFromId,
  getTreesByMatchName,
  groupNodes,
  isOutcomeDecisive,
  normaliseString,
} from "@unlikelyai-magic/utils";
import {
  filterInputGuardrailResults,
  filterOutputGuardrailResults,
} from "./guardrailUtils";
import { LiveTestPage } from "./LiveTestPage";
import type {
  ConversationPair,
  InitialMessage,
  TurnOutcomeState,
} from "./types";

export interface PreviousConversation {
  sessionId: string;
  sessionVersionId: string;
  messages: ConversationMessage[];
  isComplete: boolean;
  shouldTerminateEarly: boolean;
}

interface StaticData {
  initialConversation: ConversationPair[];
  sessionId: string;
  questionAnswers: QuestionAnswer[];
  isComplete: boolean;
  preCalculatedTreeValues: TreeValue[];
}

const getInitialMessages = (
  previousConversation: PreviousConversation
): { initialMessages: InitialMessage[] } => {
  const initialMessages: InitialMessage[] = [];
  for (const message of previousConversation.messages) {
    if (message.role === "user") {
      break;
    }
    initialMessages.push({
      id: `initial-${previousConversation.sessionId}-${initialMessages.length}`,
      content: message.content,
    });
  }

  return {
    initialMessages: initialMessages,
  };
};

const getChangedAnswers = (
  newVersion: SessionVersion,
  previousVersion: SessionVersion | null
) => {
  const previousLeaves = previousVersion?.session_product_trees
    ? getDedupedLeafNodes(
        previousVersion?.session_product_trees?.flatMap(
          (tree) => tree.leaf_nodes
        ) ?? []
      )
    : new Map<string, QuestionAnswer>();
  const newLeaves = getDedupedLeafNodes(
    newVersion?.session_product_trees?.flatMap((tree) => tree.leaf_nodes) ?? []
  );
  return Array.from(newLeaves.values()).filter(
    (node) =>
      isOutcomeDecisive(node.value) &&
      node.value !== previousLeaves.get(node.question)?.value
  );
};

const getStaticData = (
  sessionVersions: SessionVersion[],
  sessionId: string,
  allNodes: Map<string, ChangedNode>,
  isComplete: boolean,
  messages: ConversationMessage[],
  treeById: Map<string, ProductTree>
): StaticData | null => {
  if (sessionVersions.length === 0) {
    return null;
  }

  const reversedSessionVersionsList = [...sessionVersions].reverse();
  const lastEvaluatedVersion = reversedSessionVersionsList.find(
    (version) => version.evaluated
  );

  // Skip initial assistant messages to find where user messages start
  let firstUserMessageIndex = 0;
  for (let i = 0; i < messages.length; i++) {
    if (messages[i].role === "user") {
      firstUserMessageIndex = i;
      break;
    }
  }

  // Build conversation pairs from messages, starting after initial assistant messages
  const conversation: ConversationPair[] = [
    {
      id: `conversation-${sessionId}-0`,
      index: 0,
      userMessage: "",
      systemResponse: "",
      summary: "",
      changedNodes: [],
      outputGuardrailTrees: [],
      inputGuardrailTrees: [],
      sessionVersionId: "",
    },
  ];

  let sessionVersionIndex = 0;
  let previousVersion: SessionVersion | null = null;
  let pairIndex = 1;

  // Track the last computed outcomeStates so we can propagate it to the next conversation
  let lastOutcomeStates: TurnOutcomeState[] | undefined;

  for (let i = firstUserMessageIndex; i < messages.length; i++) {
    const message = messages[i];

    if (message.role === "user") {
      // Get the corresponding session version for this user message
      const version = sessionVersions[sessionVersionIndex];
      const changedAnswers = version
        ? getChangedAnswers(version, previousVersion)
        : [];
      const changedNodes: ChangedNode[] = changedAnswers.map((answer) => {
        const changedNode = allNodes.get(normaliseString(answer.question))!;
        return {
          ...changedNode,
          value: answer.value,
          reasoning: answer.reasoning ?? "",
        };
      });

      // Get outcome states from this version's session product trees
      const outcomeStates: TurnOutcomeState[] =
        version?.session_product_trees?.map((spt) => {
          const productTree = treeById.get(spt.product_tree_id);
          return {
            productTreeId: spt.product_tree_id,
            name: productTree?.name ?? spt.product_tree_id,
            type: getTreeTypeFromId(spt.product_tree_id),
            outcome: spt.outcome,
          };
        }) ?? [];

      // Update the last conversation pair with the user message
      conversation[conversation.length - 1] = {
        ...conversation[conversation.length - 1],
        userMessage: message.content,
        userMessageAt: message.created_at,
        changedNodes: changedNodes,
        sessionVersionId: version?.id ?? "",
        outcomeStates,
        contradictingQuestions: version?.contradicting_questions ?? [],
      };

      // Track last outcomeStates for propagation to next conversation
      lastOutcomeStates = outcomeStates;

      previousVersion = version;
      sessionVersionIndex++;
    } else if (message.role === "assistant") {
      // Get guardrail info from the previous session version (the one that generated this response)
      const version = sessionVersions[sessionVersionIndex - 1];

      // Separate guardrail results by type (INPUT vs OUTPUT)
      const allGuardrailResults = version?.session_guardrail_results ?? [];
      const outputGuardrailResults = filterOutputGuardrailResults(allGuardrailResults);
      const inputGuardrailResults = filterInputGuardrailResults(allGuardrailResults);

      // Find the matching output guardrail result by comparing message content
      const matchingOutputGuardrailResult = outputGuardrailResults.find(
        (result) => result.message?.content === message.content
      );

      // If the message was rephrased, include the full guardrail sequence for this message
      const outputGuardrailSequence =
        matchingOutputGuardrailResult &&
        matchingOutputGuardrailResult.sequence_id &&
        matchingOutputGuardrailResult.sequence_number > 1
          ? (outputGuardrailResults
              .filter(
                (result) =>
                  result.sequence_id === matchingOutputGuardrailResult.sequence_id
              )
              .sort((a, b) => a.sequence_number - b.sequence_number) ?? [])
          : matchingOutputGuardrailResult
            ? [matchingOutputGuardrailResult]
            : [];

      // Add a new conversation pair for the next user message
      // Also propagate the changedNodes and outcomeStates from the previous conversation
      // so the final agent message shows the same data as the user message that caused it
      const previousConv = conversation[conversation.length - 1];

      // Attach input guardrails to the previous conversation (user message turn)
      if (inputGuardrailResults.length > 0 && conversation.length > 0) {
        conversation[conversation.length - 1] = {
          ...previousConv,
          inputGuardrailTrees: inputGuardrailResults,
        };
      }

      conversation.push({
        id: `conversation-${sessionId}-${pairIndex}`,
        index: pairIndex,
        userMessage: "",
        systemResponse: message.content,
        systemResponseAt: message.created_at,
        summary: "",
        changedNodes: previousConv.changedNodes,
        outputGuardrailTrees: outputGuardrailSequence,
        inputGuardrailTrees: [],
        sessionVersionId: "",
        targetQuestions: (message as any)?.target_questions ?? [],
        outcomeStates: lastOutcomeStates,
        contradictingQuestions: previousConv.contradictingQuestions,
      });
      pairIndex++;
    }
  }

  const questionAnswers = getChangedAnswers(lastEvaluatedVersion!, null);
  const preCalculatedTreeValues: TreeValue[] =
    lastEvaluatedVersion?.session_product_trees.map((tree): TreeValue => {
      return {
        productTreeId: tree.product_tree_id,
        name: tree.product_tree_id,
        value: tree.outcome ?? Outcome.UNDECIDED,
      };
    }) ?? [];

  return {
    initialConversation: conversation,
    sessionId: sessionId,
    questionAnswers: questionAnswers,
    isComplete: isComplete,
    preCalculatedTreeValues: preCalculatedTreeValues,
  };
};

const FullPageSpinner = () => {
  return (
    <div className="flex justify-center items-center h-screen">
      <PageSpinner size={5} />
    </div>
  );
};

export const TestPage2 = ({
  onNavigateToHistory,
  previousConversation,
  onSimulationRunningChange,
  stopSimulationRef,
}: {
  onNavigateToHistory: () => void;
  previousConversation: PreviousConversation | undefined;
  onSimulationRunningChange?: (running: boolean) => void;
  stopSimulationRef?: MutableRefObject<(() => void) | null>;
}) => {
  const { data: productTrees } = useGetProductTreesQuery(null);
  const { data: initialMessagesData } = useGetInitialMessagesQuery();
  const { data: sessionVersions, refetch: refetchSessionVersions } =
    useGetSessionAllVersionsQuery(
      { sessionId: previousConversation?.sessionId ?? "" },
      { skip: !previousConversation?.sessionId, refetchOnMountOrArgChange: true }
    );

  useEffect(() => {
    if (previousConversation?.sessionId) {
      refetchSessionVersions();
    }
  }, [previousConversation, refetchSessionVersions]);
  const suggestionTrees = productTrees?.data ?? [];

  const situationTrees = cleanTreeNames(
    getTreesByMatchName(productTrees?.data ?? [], "situation-")
  );
  const segmentTrees = cleanTreeNames(
    getTreesByMatchName(productTrees?.data ?? [], "segment-")
  );

  const allTrees = deduplicateTreesByQuestion([
    ...situationTrees,
    ...segmentTrees,
    ...suggestionTrees,
  ]);

  const treeById: Map<string, ProductTree> = useMemo(() => {
    if (!productTrees) return new Map<string, ProductTree>();
    return allTrees.reduce(
      (acc: Map<string, ProductTree>, tree: ProductTree) => {
        acc.set(tree.id, tree);
        return acc;
      },
      new Map<string, ProductTree>()
    );
  }, [productTrees, allTrees]);

  const allNodes = groupNodes(productTrees?.data ?? []);

  // Don't render LiveTestPage until product trees and initial messages are loaded
  if (!productTrees?.data?.length || !initialMessagesData?.messages) {
    return <FullPageSpinner />;
  }

  if (!previousConversation) {
    const systemState = getSystemStateValues(allTrees, [], []);

    return (
      <LiveTestPage
        onNavigateToHistory={onNavigateToHistory}
        onSimulationRunningChange={onSimulationRunningChange}
        stopSimulationRef={stopSimulationRef}
        initialData={{
          treeById: treeById,
          allNodes: allNodes,
          staticData: null,
          systemState: systemState,
          initialMessages: initialMessagesData.messages.map(
            (content, index) => ({
              id: `initial-default-${index}`,
              content,
            })
          ),
        }}
      />
    );
  }

  const { initialMessages } = getInitialMessages(previousConversation);

  // Don't render LiveTestPage until product trees are loaded
  if (!sessionVersions?.session_versions?.length) {
    return <FullPageSpinner />;
  }

  const staticData = getStaticData(
    sessionVersions?.session_versions ?? [],
    previousConversation.sessionId,
    allNodes,
    previousConversation.isComplete,
    previousConversation.messages,
    treeById
  );
  const systemState = getSystemStateValues(
    allTrees,
    staticData?.questionAnswers ?? [],
    staticData?.preCalculatedTreeValues ?? []
  );

  return (
    <LiveTestPage
      onNavigateToHistory={onNavigateToHistory}
      onSimulationRunningChange={onSimulationRunningChange}
      stopSimulationRef={stopSimulationRef}
      initialData={{
        treeById: treeById,
        allNodes: allNodes,
        staticData: staticData,
        systemState: systemState,
        initialMessages: initialMessages,
        shouldTerminateEarly: previousConversation.shouldTerminateEarly,
      }}
    />
  );
};
